/* =============================================================================
   AUCTION TERMS SLIDES — the one renderer
   -----------------------------------------------------------------------------
   Turns a slide (a row of `terms_slides`, as GET /api/terms-slides/* hands it
   out, or as it rides on the AUCTION-TERMS `current-lot-updated` broadcast) into
   DOM. Used by:

     · the host page's terms widget (the stage + the thumbnail strip) and the
       deck editor's live preview
     · the live page's viewer slideshow and its Terms lightbox
     · the projector

   so a slide looks the same on every screen because it IS the same markup.
   Styles: assets/mhv-terms-slides.css. Rules + seed copy: lib/terms-slides.js.

   A slide on the wire:
     { kind: 'text',  content: { title, hero, heroLabel, columns:[{heading, points[]}], footnote, logo } }
     { kind: 'image', url, alt, content: { caption } }

   Inline markup inside any text: **teal emphasis**, !!pink warning!!,
   ~~small muted~~, and a newline breaks the line. Everything is HTML-escaped first — a point is text
   somebody typed, never markup.

   Tokens ({{bp}} and friends) arrive already substituted on every read that
   has a session. `resolve()` exists ONLY for the editor's preview, which edits
   the raw text and wants to see it as the room will.
   ============================================================================= */
(function () {
  'use strict';

  // The torn-paper badge (plum line art, solid white fill, transparent outside) —
  // the CSS strokes it in white along the alpha edge.
  var LOGO_URL = 'https://cdn.shopify.com/s/files/1/0073/9101/9067/files/GUAC-LOGO-PLUM-WHITE-SOLID.png?v=1789869691';

  function esc(s) {
    if (s == null) return '';
    return String(s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  /** Escaped text with the two accents and line breaks applied. */
  function inline(text) {
    var out = esc(text);
    out = out.replace(/\*\*([^*]+?)\*\*/g, '<em class="ts-em">$1</em>');
    out = out.replace(/!!([^!]+?)!!/g, '<em class="ts-warn">$1</em>');
    out = out.replace(/~~([^~]+?)~~/g, '<small class="ts-small">$1</small>');
    out = out.replace(/\n/g, '<br>');
    return out;
  }

  /** Editor-only: substitute {{tokens}} the way the server does. */
  function resolve(content, tokens) {
    if (!content || !tokens) return content;
    function sub(s) {
      if (typeof s !== 'string') return s;
      return s.replace(/\{\{\s*([a-z0-9_]+)\s*\}\}/gi, function (m, k) {
        k = String(k).toLowerCase();
        return Object.prototype.hasOwnProperty.call(tokens, k) ? String(tokens[k]) : m;
      });
    }
    var out = {};
    for (var k in content) if (Object.prototype.hasOwnProperty.call(content, k)) out[k] = content[k];
    ['title', 'hero', 'heroLabel', 'footnote', 'caption'].forEach(function (k) { out[k] = sub(out[k]); });
    if (Array.isArray(out.columns)) {
      out.columns = out.columns.map(function (c) {
        return { heading: sub(c && c.heading), points: (c && Array.isArray(c.points) ? c.points : []).map(sub) };
      });
    }
    return out;
  }

  function textStageHtml(c) {
    c = c || {};
    var cols = (Array.isArray(c.columns) ? c.columns : []).filter(function (col) {
      return col && ((col.heading && String(col.heading).trim()) || (Array.isArray(col.points) && col.points.length));
    }).slice(0, 2);
    var maxPoints = 0;
    cols.forEach(function (col) { maxPoints = Math.max(maxPoints, (col.points || []).length); });

    var h = '<div class="ts-frame' + (c.align === 'center' ? ' ts-frame--center' : '') + '">';
    // ⚠️ DIVs, not <header>/<footer>/<h2>: the host and projector pages hide the
    // Shopify chrome by ELEMENT name (host-5.css `header, .header, …`,
    // projector-4.css `.projector-template header, footer`), which took the
    // slide's title and foot with it on every screen but the local preview.
    if (c.title) h += '<div class="ts-head"><div class="ts-title">' + inline(c.title) + '</div></div>';
    if (c.hero) {
      h += '<div class="ts-hero"><span class="ts-hero-value">' + inline(c.hero) + '</span>';
      if (c.heroLabel) h += '<span class="ts-hero-label">' + inline(c.heroLabel) + '</span>';
      h += '</div>';
    }
    h += '<div class="ts-body' + (c.logo ? ' ts-body--logo' : '') + '">';
    if (c.logo) {
      h += '<div class="ts-logo"><img src="' + esc(LOGO_URL) + '" alt="Granite Union Auction Company"></div>';
    }
    if (cols.length) {
      h += '<div class="ts-cols ts-cols--' + cols.length + '" data-max-points="' + Math.min(8, maxPoints) + '">';
      cols.forEach(function (col) {
        h += '<div class="ts-col">';
        if (col.heading && String(col.heading).trim()) h += '<div class="ts-col-head">' + inline(col.heading) + '</div>';
        if (col.points && col.points.length) {
          h += '<ul class="ts-points">';
          col.points.forEach(function (p) { h += '<li class="ts-point">' + inline(p) + '</li>'; });
          h += '</ul>';
        }
        h += '</div>';
      });
      h += '</div>';
    }
    h += '</div>';
    h += '<div class="ts-foot' + (c.footnote ? '' : ' ts-foot--empty') + '">' +
      '<span class="ts-brand">Granite Union Auction Co.</span>' +
      '<span class="ts-footnote">' + inline(c.footnote || '') + '</span></div>';
    h += '</div>';
    return h;
  }

  function imageStageHtml(slide) {
    var c = slide.content || {};
    var h = '<img class="ts-img" src="' + esc(slide.url) + '" alt="' + esc(slide.alt || c.caption || 'Auction terms') + '" draggable="false">';
    if (c.caption) h += '<div class="ts-caption">' + inline(c.caption) + '</div>';
    return h;
  }

  /** The inner HTML of a `.terms-slide` (the stage and everything on it). */
  function html(slide, opts) {
    opts = opts || {};
    if (!slide) return '<div class="ts-stage"><div class="ts-frame"><div class="ts-empty">No slides in this deck</div></div></div>';
    var content = opts.tokens ? resolve(slide.content, opts.tokens) : (slide.content || {});
    if (slide.kind === 'image') return '<div class="ts-stage">' + imageStageHtml({ url: slide.url, alt: slide.alt, content: content }) + '</div>';
    return '<div class="ts-stage">' + textStageHtml(content) + '</div>';
  }

  // The scale ladder fit() walks down. Every type size on the stage is
  // `calc(Ncqw * var(--ts-scale))`, so one step shrinks the whole slide evenly.
  var FIT_STEPS = [1, 0.97, 0.94, 0.91, 0.88, 0.85, 0.82, 0.79, 0.76, 0.73, 0.7, 0.66, 0.62];

  /**
   * Shrink a rendered slide's type until nothing overflows the body. Because
   * every measurement is in container units, an overflow is a property of the
   * CONTENT, not of the box it is in — so the answer found on a 640px preview
   * holds on a 1080p projector. Safe to call again (idempotent for one slide);
   * called automatically once the element is in the DOM.
   */
  function fit(el) {
    if (!el || !el.querySelector) return;
    var stage = el.querySelector('.ts-stage');
    var body = el.querySelector('.ts-body');
    var frame = el.querySelector('.ts-frame');
    if (!stage || !body || !frame || !stage.clientWidth) return;
    // Below ~120px wide the measurements are rounding noise — a thumbnail
    // inherits whatever the full-size render decided (data-ts-scale), or 1.
    if (stage.clientWidth < 120) return;
    for (var i = 0; i < FIT_STEPS.length; i++) {
      stage.style.setProperty('--ts-scale', String(FIT_STEPS[i]));
      var bodyOk = body.scrollHeight <= body.clientHeight + 1;
      var frameOk = frame.scrollHeight <= frame.clientHeight + 1;
      if (bodyOk && frameOk) break;
    }
    el.setAttribute('data-ts-scale', stage.style.getPropertyValue('--ts-scale') || '1');
  }

  function scheduleFit(el, tries) {
    tries = tries == null ? 6 : tries;
    if (typeof requestAnimationFrame !== 'function') return;
    requestAnimationFrame(function () {
      if (!el.isConnected) { if (tries > 0) scheduleFit(el, tries - 1); return; }
      fit(el);
      // ⚠️ Measured with the fallback face, a slide fits a WIDER font and comes
      // out a size too small once Patua One / Assistant arrive. Fit again when
      // the fonts are in (a no-op when they were already cached).
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(function () { if (el.isConnected) fit(el); });
      }
      // ...and again when an image (the Welcome logo) arrives: measured before it
      // loads, the slide is a different, shorter slide.
      var imgs = el.querySelectorAll('img');
      for (var i = 0; i < imgs.length; i++) {
        if (!imgs[i].complete) imgs[i].addEventListener('load', function () { if (el.isConnected) fit(el); }, { once: true });
      }
    });
  }

  /** A `.terms-slide` element. `opts.className` adds classes (e.g. 'slide'). */
  function render(slide, opts) {
    opts = opts || {};
    var el = document.createElement('div');
    el.className = 'terms-slide' +
      (slide && slide.kind === 'image' ? ' terms-slide--image' : '') +
      (slide ? '' : ' terms-slide--empty') +
      (opts.className ? ' ' + opts.className : '');
    el.innerHTML = html(slide, opts);
    // A known scale (a thumbnail of a slide already fitted at full size, or a
    // wire payload that carries one) skips the measuring.
    var known = opts.scale || (slide && slide.tsScale);
    if (known) {
      var st = el.querySelector('.ts-stage');
      if (st) st.style.setProperty('--ts-scale', String(known));
      el.setAttribute('data-ts-scale', String(known));
    } else if (slide && slide.kind !== 'image' && opts.fit !== false) {
      scheduleFit(el);
    }
    return el;
  }

  /** True when a current-lot payload / entry is the terms deck rather than a lot. */
  function isTermsPayload(data) {
    return !!(data && (data.sku === 'AUCTION-TERMS' || data.action === 'auction-terms' ||
      (data.product && data.product.isAuctionTerms)));
  }

  /** True when an `images[]` entry is a terms slide (carries a kind) rather than a photo url. */
  function isSlideEntry(entry) {
    return !!(entry && typeof entry === 'object' && (entry.kind === 'text' || entry.kind === 'image') && entry.content !== undefined);
  }

  /**
   * The `product.images[]` payload the host broadcasts for a deck. Image slides
   * keep `url`/`alt` so anything that never learned about text slides still
   * shows a picture; text slides carry their content and no url.
   */
  function toImages(slides) {
    return (slides || []).filter(function (s) { return s && s.enabled !== false; }).map(function (s) {
      return {
        kind: s.kind,
        url: s.kind === 'image' ? s.url : null,
        alt: s.alt || (s.content && (s.content.title || s.content.caption)) || 'Auction terms',
        content: s.content || {},
        imageId: s.imageId || null
      };
    });
  }

  /**
   * One slideshow `.slide` element for an `images[]` entry: a rendered terms
   * slide when the entry is one, a plain <img> otherwise (a product photo).
   */
  function slideElement(entry, index, extraClass) {
    var cls = 'slide' + (extraClass ? ' ' + extraClass : '');
    if (isSlideEntry(entry)) {
      var el = render(entry, { className: cls });
      el.setAttribute('data-index', String(index));
      return el;
    }
    var img = document.createElement('img');
    img.src = entry && (entry.url || entry.src) || '';
    img.alt = (entry && entry.alt) || ('Image ' + (index + 1));
    img.className = cls;
    img.setAttribute('data-index', String(index));
    return img;
  }

  window.MHVTermsSlides = {
    LOGO_URL: LOGO_URL,
    esc: esc,
    inline: inline,
    resolve: resolve,
    html: html,
    render: render,
    fit: fit,
    isTermsPayload: isTermsPayload,
    isSlideEntry: isSlideEntry,
    toImages: toImages,
    slideElement: slideElement
  };
})();
